/**
 * Columns picker entry-point with preset switcher.
 *
 * Click → dropdown showing:
 *   - Saved presets (org-shared, per tab). Click name → apply.
 *     Click trash icon → first click arms (red "Confirm?" pill);
 *     second click within 4s fires the delete. Click outside / wait
 *     4s reverts. This is the user-requested double-confirmation.
 *   - Empty-state "You have no saved presets" if none exist yet.
 *   - "Edit columns" footer always present → opens the ColumnsModal.
 *
 * Inside the modal the user saves the current visibility + ORDER +
 * WIDTHS as a new preset. Widths come from StatsTable's resize state
 * via the `currentWidths` prop.
 */

import { useEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";
import { ColumnsModal } from "@/components/ColumnsModal";
import {
  useClearDefaultColumnPreset,
  useColumnPresets,
  useDeleteColumnPreset,
  useSetDefaultColumnPreset,
} from "@/lib/api-hooks";
import { resolvePresetColumns, type ColumnKey } from "@/lib/columns-config";

type Tab = "campaigns" | "ad_groups" | "keywords" | "rules";

export function ColumnsButton({
  tab,
  visible,
  onChange,
  allowedKeys,
  currentWidths,
  onApplyWidths,
}: {
  tab: Tab;
  visible: ColumnKey[];
  onChange: (next: ColumnKey[]) => void;
  allowedKeys?: ColumnKey[];
  /** Live width map from StatsTable resize state, persisted into a
   *  preset when user clicks "Save as preset" inside the modal. */
  currentWidths?: Record<string, number>;
  /** Called with a preset's widths when the user applies that preset
   *  — StatsTable adopts them into its resize state. */
  onApplyWidths?: (widths: Record<string, number>) => void;
}) {
  const presetsQuery = useColumnPresets(tab);
  const deletePreset = useDeleteColumnPreset();
  const setDefaultPreset = useSetDefaultColumnPreset();
  const clearDefaultPreset = useClearDefaultColumnPreset();
  const [editOpen, setEditOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const confirmTimerRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const presets = presetsQuery.data ?? [];

  // Click-outside closes the dropdown; Escape comes through the layer stack.
  useLayer(open, () => {
    setOpen(false);
    setConfirmDeleteId(null);
  });
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setConfirmDeleteId(null);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  // Auto-cancel confirm-state after 4s if the user doesn't follow
  // through with the second click.
  useEffect(() => {
    if (confirmDeleteId === null) return;
    if (confirmTimerRef.current !== null) {
      window.clearTimeout(confirmTimerRef.current);
    }
    confirmTimerRef.current = window.setTimeout(() => {
      setConfirmDeleteId(null);
    }, 4000);
    return () => {
      if (confirmTimerRef.current !== null) {
        window.clearTimeout(confirmTimerRef.current);
      }
    };
  }, [confirmDeleteId]);

  const applyPreset = (id: number) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    onChange(resolvePresetColumns(tab, p.columns, allowedKeys));
    // Adopt the preset's widths into StatsTable's resize state.
    if (onApplyWidths) onApplyWidths(p.widths ?? {});
    setOpen(false);
  };

  const onTrashClick = (id: number, e: React.MouseEvent) => {
    // Stop the row-level click so trash never accidentally applies the
    // preset.
    e.stopPropagation();
    if (confirmDeleteId === id) {
      // Second click — confirmed.
      deletePreset.mutate(id);
      setConfirmDeleteId(null);
      return;
    }
    // First click — arm.
    setConfirmDeleteId(id);
  };

  const onStarClick = (
    id: number,
    isDefault: boolean,
    e: React.MouseEvent,
  ) => {
    // Don't let the row-level click handler also fire (= applying the
    // preset). A star toggle is a separate intent.
    e.stopPropagation();
    if (setDefaultPreset.isPending || clearDefaultPreset.isPending) return;
    if (isDefault) {
      // Already default → clear it.
      clearDefaultPreset.mutate(id);
    } else {
      // Not default yet → set as default (backend auto-replaces the
      // user's previous default for this tab).
      setDefaultPreset.mutate(id);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        {I.sliders}
        <span>Columns</span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 260,
            background: "var(--c-surface, white)",
            border: "1px solid var(--c-border, #ddd)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            zIndex: 50,
            padding: 4,
          }}
        >
          {presets.length === 0 && (
            <div
              style={{
                padding: "8px 10px",
                fontSize: 12,
                color: "var(--c-text-3, #777)",
              }}
            >
              You have no saved presets
            </div>
          )}
          {presets.map((p) => {
            const confirming = confirmDeleteId === p.id;
            const isDefault = p.is_default;
            const starBusy =
              (setDefaultPreset.isPending &&
                setDefaultPreset.variables === p.id) ||
              (clearDefaultPreset.isPending &&
                clearDefaultPreset.variables === p.id);
            return (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  borderRadius: 6,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background =
                    "var(--c-surface-alt, #f5f5f5)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                <button
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  style={{
                    flex: 1,
                    textAlign: "left",
                    padding: "6px 10px",
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                    fontSize: 13,
                    color: "var(--c-text, #222)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>{p.name}</span>
                  {isDefault && (
                    <span
                      title="Auto-loaded on page open"
                      style={{
                        fontSize: 9.5,
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "var(--c-accent-600, #2962ff)",
                        background: "var(--c-accent-50, #eef3ff)",
                        padding: "2px 6px",
                        borderRadius: 4,
                      }}
                    >
                      Default
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  title={
                    isDefault
                      ? "Currently your default — click to clear"
                      : "Save as Default — auto-applied on page open"
                  }
                  onClick={(e) => onStarClick(p.id, isDefault, e)}
                  disabled={starBusy}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 6px",
                    background: "transparent",
                    border: "1px solid transparent",
                    borderRadius: 6,
                    cursor: starBusy ? "wait" : "pointer",
                    color: isDefault
                      ? "var(--c-accent-600, #2962ff)"
                      : "var(--c-text-4, #bbb)",
                    fontSize: 11,
                  }}
                >
                  {isDefault ? I.starFilled : I.star}
                </button>
                <button
                  type="button"
                  title={
                    confirming
                      ? "Click again to confirm delete"
                      : "Delete preset"
                  }
                  onClick={(e) => onTrashClick(p.id, e)}
                  disabled={deletePreset.isPending}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 8px",
                    marginRight: 4,
                    background: confirming ? "#ffe8e8" : "transparent",
                    border: confirming ? "1px solid #d94545" : "1px solid transparent",
                    borderRadius: 6,
                    cursor: deletePreset.isPending ? "not-allowed" : "pointer",
                    color: confirming ? "#d94545" : "var(--c-text-3, #999)",
                    fontSize: 11,
                    fontWeight: confirming ? 600 : 400,
                  }}
                >
                  {confirming ? "Confirm?" : I.trash}
                </button>
              </div>
            );
          })}
          <div
            style={{
              borderTop: "1px solid var(--c-divider, #eee)",
              margin: "4px 0",
            }}
          />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setEditOpen(true);
            }}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "8px 10px",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              fontSize: 13,
              color: "var(--c-accent-600, #2962ff)",
              borderRadius: 6,
              fontWeight: 500,
            }}
          >
            Edit Columns
          </button>
        </div>
      )}

      <ColumnsModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        visible={visible}
        onChange={onChange}
        tab={tab}
        allowedKeys={allowedKeys}
        currentWidths={currentWidths}
      />
    </div>
  );
}
